import { ConfirmModal } from '@/components/admin/confirm-modal';
import { AnonymizedBadge, RoleBadge, StatusBadge } from '@/components/admin/status-badge';
import { type AdminUserDetail, admin } from '@/lib/admin-api';
import type { UserRole, UserStatus } from '@/lib/auth-client';
import { routes } from '@/lib/routes';
import { ArrowLeft, Key, Loader2, LogOut, Shield, ShieldOff, UserMinus, UserX } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

export default function AdminUserDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showAnonymizeModal, setShowAnonymizeModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'status' | 'role';
    value: UserStatus | UserRole;
  } | null>(null);
  const [anonymizeReason, setAnonymizeReason] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await admin.users.get(id);
      setUser(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleStatusChange = async () => {
    if (!id || !pendingAction || pendingAction.type !== 'status') return;
    setIsProcessing(true);
    try {
      const updated = await admin.users.updateStatus(id, {
        status: pendingAction.value as UserStatus
      });
      setUser(updated);
      setShowStatusModal(false);
      setPendingAction(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRoleChange = async () => {
    if (!id || !pendingAction || pendingAction.type !== 'role') return;
    setIsProcessing(true);
    try {
      const updated = await admin.users.updateRole(id, {
        role: pendingAction.value as UserRole
      });
      setUser(updated);
      setShowRoleModal(false);
      setPendingAction(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAnonymize = async () => {
    if (!id || !anonymizeReason.trim()) return;
    setIsProcessing(true);
    try {
      await admin.users.anonymize(id, { reason: anonymizeReason });
      navigate(routes.admin.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to anonymize user');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleForceLogout = async () => {
    if (!id) return;
    setIsProcessing(true);
    try {
      await admin.users.forceLogout(id);
      await fetchUser();
      setShowLogoutModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to logout user');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePasswordUpdate = async () => {
    if (!id || !newPassword) return;
    if (newPassword.length < 8) {
      setPasswordError(t('auth.weakPassword'));
      return;
    }
    setIsProcessing(true);
    setPasswordError('');
    try {
      await admin.users.updatePassword(id, { password: newPassword });
      setShowPasswordModal(false);
      setNewPassword('');
      await fetchUser();
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to update password');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue animate-spin" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-4">
        <Link
          to={routes.admin.users}
          className="inline-flex items-center gap-2 text-sepia/70 hover:text-sepia"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('admin.users.backToList')}
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
          {error || 'User not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to={routes.admin.users}
        className="inline-flex items-center gap-2 text-sepia/70 hover:text-sepia"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('admin.users.backToList')}
      </Link>

      {/* User Info Card */}
      <div className="bg-white rounded-xl border border-gray-warm p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-ink">
                {user.displayName || user.username}
              </h1>
              <RoleBadge role={user.role} />
              <StatusBadge status={user.status} />
              <AnonymizedBadge anonymizedAt={user.anonymizedAt} />
            </div>
            {user.displayName && <p className="text-sepia/60 mt-0.5">@{user.username}</p>}
            <p className="text-sepia/70 mt-1">{user.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6 pt-6 border-t border-gray-warm">
          <div>
            <p className="text-xs text-sepia/70 uppercase">{t('admin.users.country')}</p>
            <p className="font-medium">{user.country}</p>
          </div>
          <div>
            <p className="text-xs text-sepia/70 uppercase">{t('admin.users.locale')}</p>
            <p className="font-medium">{user.locale}</p>
          </div>
          <div>
            <p className="text-xs text-sepia/70 uppercase">{t('admin.users.joined')}</p>
            <p className="font-medium">{new Date(user.createdAt).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-xs text-sepia/70 uppercase">{t('admin.users.emailVerified')}</p>
            <p className="font-medium">{user.emailVerified ? 'Yes' : 'No'}</p>
          </div>
        </div>

        {user.bio && (
          <div className="mt-6 pt-6 border-t border-gray-warm">
            <p className="text-xs text-sepia/70 uppercase mb-2">{t('admin.users.bio')}</p>
            <p className="text-sepia">{user.bio}</p>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-warm p-4">
          <p className="text-2xl font-semibold text-ink">{user._count.sentLetters}</p>
          <p className="text-xs text-sepia/70">{t('admin.users.sentLetters')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-warm p-4">
          <p className="text-2xl font-semibold text-ink">{user._count.receivedLetters}</p>
          <p className="text-xs text-sepia/70">{t('admin.users.receivedLetters')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-warm p-4">
          <p className="text-2xl font-semibold text-ink">{user._count.reportsMade}</p>
          <p className="text-xs text-sepia/70">{t('admin.users.reportsMade')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-warm p-4">
          <p className="text-2xl font-semibold text-red-600">{user._count.reportsReceived}</p>
          <p className="text-xs text-sepia/70">{t('admin.users.reportsReceived')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-warm p-4">
          <p className="text-2xl font-semibold text-ink">{user._count.sessions}</p>
          <p className="text-xs text-sepia/70">{t('admin.users.activeSessions')}</p>
        </div>
      </div>

      {/* Actions */}
      {!user.isAnonymized && (
        <div className="bg-white rounded-xl border border-gray-warm p-6">
          <h2 className="text-lg font-medium text-ink mb-4">{t('admin.users.actions')}</h2>
          <div className="flex flex-wrap gap-3">
            {/* Status Actions */}
            {user.status !== 'SUSPENDED' && (
              <button
                type="button"
                onClick={() => {
                  setPendingAction({ type: 'status', value: 'SUSPENDED' });
                  setShowStatusModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors"
              >
                <UserMinus className="w-4 h-4" />
                {t('admin.users.suspend')}
              </button>
            )}
            {user.status === 'SUSPENDED' && (
              <button
                type="button"
                onClick={() => {
                  setPendingAction({ type: 'status', value: 'ACTIVE' });
                  setShowStatusModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
              >
                {t('admin.users.activate')}
              </button>
            )}
            {user.status !== 'BANNED' && (
              <button
                type="button"
                onClick={() => {
                  setPendingAction({ type: 'status', value: 'BANNED' });
                  setShowStatusModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
              >
                <UserX className="w-4 h-4" />
                {t('admin.users.ban')}
              </button>
            )}

            {/* Role Actions */}
            {user.role === 'USER' ? (
              <button
                type="button"
                onClick={() => {
                  setPendingAction({ type: 'role', value: 'ADMIN' });
                  setShowRoleModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
              >
                <Shield className="w-4 h-4" />
                {t('admin.users.promoteAdmin')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setPendingAction({ type: 'role', value: 'USER' });
                  setShowRoleModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-warm/30 text-sepia rounded-lg hover:bg-gray-warm/50 transition-colors"
              >
                <ShieldOff className="w-4 h-4" />
                {t('admin.users.demoteUser')}
              </button>
            )}

            {/* Force Logout */}
            {user._count.sessions > 0 && (
              <button
                type="button"
                onClick={() => setShowLogoutModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-warm/30 text-sepia rounded-lg hover:bg-gray-warm/50 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('admin.users.forceLogout')}
              </button>
            )}

            {/* Change Password */}
            <button
              type="button"
              onClick={() => setShowPasswordModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue/10 text-blue rounded-lg hover:bg-blue/20 transition-colors"
            >
              <Key className="w-4 h-4" />
              {t('admin.users.changePassword')}
            </button>

            {/* Anonymize */}
            <button
              type="button"
              onClick={() => setShowAnonymizeModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t('admin.users.anonymize')}
            </button>
          </div>
        </div>
      )}

      {/* Anonymization Info */}
      {user.isAnonymized && (
        <div className="bg-cream/50 border border-gray-warm rounded-xl p-6">
          <h2 className="text-lg font-medium text-ink mb-2">{t('admin.users.anonymizedInfo')}</h2>
          <p className="text-sepia/80">
            {t('admin.users.anonymizedOn', {
              date: user.anonymizedAt ? new Date(user.anonymizedAt).toLocaleDateString() : 'Unknown'
            })}
          </p>
        </div>
      )}

      {/* Status Change Modal */}
      <ConfirmModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setPendingAction(null);
        }}
        onConfirm={handleStatusChange}
        title={
          pendingAction?.value === 'BANNED'
            ? t('admin.users.confirmBan')
            : pendingAction?.value === 'SUSPENDED'
              ? t('admin.users.confirmSuspend')
              : t('admin.users.confirmActivate')
        }
        description={t('admin.users.statusChangeWarning', { status: pendingAction?.value })}
        confirmText={t('admin.common.confirm')}
        variant={pendingAction?.value === 'BANNED' ? 'danger' : 'warning'}
        isLoading={isProcessing}
      />

      {/* Role Change Modal */}
      <ConfirmModal
        isOpen={showRoleModal}
        onClose={() => {
          setShowRoleModal(false);
          setPendingAction(null);
        }}
        onConfirm={handleRoleChange}
        title={
          pendingAction?.value === 'ADMIN'
            ? t('admin.users.confirmPromote')
            : t('admin.users.confirmDemote')
        }
        description={t('admin.users.roleChangeWarning', { role: pendingAction?.value })}
        confirmText={t('admin.common.confirm')}
        variant="warning"
        isLoading={isProcessing}
      />

      {/* Force Logout Modal */}
      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleForceLogout}
        title={t('admin.users.confirmLogout')}
        description={t('admin.users.logoutWarning')}
        confirmText={t('admin.users.forceLogout')}
        variant="warning"
        isLoading={isProcessing}
      />

      {/* Anonymize Modal */}
      <ConfirmModal
        isOpen={showAnonymizeModal}
        onClose={() => {
          setShowAnonymizeModal(false);
          setAnonymizeReason('');
        }}
        onConfirm={handleAnonymize}
        title={t('admin.users.confirmAnonymize')}
        description={
          <div className="space-y-4">
            <p>{t('admin.users.anonymizeWarning')}</p>
            <ul className="text-sm list-disc pl-4 space-y-1">
              <li>{t('admin.users.anonymizeList.email')}</li>
              <li>{t('admin.users.anonymizeList.username')}</li>
              <li>{t('admin.users.anonymizeList.displayName')}</li>
              <li>{t('admin.users.anonymizeList.bio')}</li>
              <li>{t('admin.users.anonymizeList.sessions')}</li>
              <li>{t('admin.users.anonymizeList.blocks')}</li>
            </ul>
            <p className="font-medium text-green-700">{t('admin.users.lettersPreserved')}</p>
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-sepia mb-1">
                {t('admin.users.anonymizeReason')}
              </label>
              <textarea
                id="reason"
                value={anonymizeReason}
                onChange={(e) => setAnonymizeReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={3}
                placeholder={t('admin.users.anonymizeReasonPlaceholder')}
              />
            </div>
          </div>
        }
        confirmText={t('admin.users.anonymize')}
        variant="danger"
        isLoading={isProcessing}
      />

      {/* Password Update Modal */}
      <ConfirmModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setNewPassword('');
          setPasswordError('');
        }}
        onConfirm={handlePasswordUpdate}
        title={t('admin.users.changePasswordTitle')}
        description={
          <div className="space-y-4">
            <p>{t('admin.users.changePasswordDesc')}</p>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-sepia mb-1">
                {t('auth.newPassword')}
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordError('');
                }}
                className="w-full px-3 py-2 border border-gray-warm rounded-lg focus:ring-2 focus:ring-blue focus:border-blue"
                placeholder={t('admin.users.newPasswordPlaceholder')}
                minLength={8}
              />
              {passwordError && <p className="text-sm text-red-600 mt-1">{passwordError}</p>}
              <p className="text-xs text-sepia/60 mt-1">{t('admin.users.passwordHint')}</p>
            </div>
          </div>
        }
        confirmText={t('admin.users.updatePassword')}
        variant="warning"
        isLoading={isProcessing}
      />
    </div>
  );
}
